// TopicCard — one grid tile on /help.
//
// Displays title, short summary, est. total minutes, step count, and a
// freshness chip that turns yellow when the YAML is stale (>90 days).
// Clicking routes to /help/:slug. Keyboard focus + Enter land the same
// navigation (standard anchor semantics — no custom key handler).

import { Link } from 'react-router-dom'
import { ArrowRight, Clock, Layers } from 'lucide-react'

import type { TopicSummary } from './types'
import {
  categoryLabel,
  daysSinceVerified,
  isStale,
} from './topic-utils'

interface TopicCardProps {
  topic: TopicSummary
}

export function TopicCard({ topic }: TopicCardProps) {
  const days = daysSinceVerified(topic.last_verified_at)
  const stale = isStale(topic.last_verified_at)
  return (
    <Link
      to={`/help/${encodeURIComponent(topic.slug)}`}
      data-testid={`help-topic-card-${topic.slug}`}
      aria-label={`Open help walkthrough for ${topic.title}`}
      className="group flex h-full flex-col justify-between rounded-xl border border-hairline bg-surface-raised p-5 shadow-sm transition hover:border-butler-500 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-butler-500/60 dark:border-slate-700 dark:bg-slate-900"
    >
      <div>
        <div className="mb-3 flex items-center justify-between gap-2">
          <span className="inline-flex items-center rounded-full bg-butler-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-butler-600 dark:bg-butler-500/15 dark:text-butler-300">
            {categoryLabel(topic.category)}
          </span>
          {stale ? (
            <span
              className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-500/20 dark:text-amber-200"
              title={`Last verified ${days} days ago`}
            >
              Verify refresh
            </span>
          ) : null}
        </div>
        <h3 className="text-base font-semibold text-ink group-hover:text-butler-600 dark:text-slate-100">
          {topic.title}
        </h3>
        <p className="mt-1.5 line-clamp-3 text-sm text-ink-soft dark:text-slate-400">
          {topic.summary}
        </p>
      </div>
      <div className="mt-4 flex items-center justify-between text-[11px] text-ink-soft dark:text-slate-500">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1">
            <Clock aria-hidden="true" className="h-3 w-3" />
            {topic.est_total_minutes} min
          </span>
          <span className="inline-flex items-center gap-1">
            <Layers aria-hidden="true" className="h-3 w-3" />
            {topic.step_count} {topic.step_count === 1 ? 'step' : 'steps'}
          </span>
        </div>
        <span className="inline-flex items-center gap-1 font-medium text-butler-600 opacity-0 transition group-hover:opacity-100 dark:text-butler-300">
          Open <ArrowRight aria-hidden="true" className="h-3 w-3" />
        </span>
      </div>
    </Link>
  )
}
